/**
 * LeadDetailSchedule
 * -----------------
 * Schedule tab for the Lead Detail panel (Coach Console → click a client row).
 * Extracted from CoachDashboard.jsx (2026-06-08) as part of the P3
 * leadDetail/{Info,Billing,Schedule} split. JSX is copied VERBATIM — no
 * styling or behavioral changes. Backed by /api/coach/clients/{user_id}/ensure-self-athlete
 * and AthleteSchedulePanel.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Plus, Loader2, User, ChevronRight } from "lucide-react";
import AthleteSchedulePanel from "@/components/coach/AthleteSchedulePanel";
import CoverageDot from "@/components/coach/leadDetail/CoverageDot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadDetailSchedule({
    lead,
    scheduleAthleteId,
    setScheduleAthleteId,
    onAddAthlete,
    onAthletesChanged,
}) {
    const athletes = lead.athletes || [];
    const [ensuring, setEnsuring] = useState(false);

    // Convert this client into their own schedulable athlete (idempotent).
    // For adult clients who don't have a kid record — we still want to be
    // able to book sessions for them.
    const ensureSelfAthlete = async () => {
        setEnsuring(true);
        try {
            const res = await axios.post(
                `${API}/coach/clients/${lead.user_id}/ensure-self-athlete`,
                {},
                { withCredentials: true }
            );
            const newAthId = res.data?.athlete?.athlete_id;
            toast.success(res.data?.created ? "Ready to schedule" : "Already set up");
            // Refresh the lead so the new athlete shows up in the picker.
            await onAthletesChanged?.();
            if (newAthId) setScheduleAthleteId(newAthId);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't enable scheduling");
        } finally {
            setEnsuring(false);
        }
    };

    // No athletes on this account → coach can either add a kid (family
    // account) OR mark the client themselves as the athlete (adult account).
    if (athletes.length === 0) {
        return (
            <div className="p-6">
                <div className="border border-dashed border-zinc-800 rounded-lg px-5 py-8 text-center">
                    <CalendarDays className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
                    <p className="text-sm text-zinc-300 mb-4 leading-snug">
                        No athletes on this profile yet. Pick how this client trains:
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2 justify-center">
                        <button
                            type="button"
                            data-testid="lead-schedule-train-self"
                            onClick={ensureSelfAthlete}
                            disabled={ensuring}
                            className="inline-flex items-center justify-center gap-1.5 text-[10px] tracking-[0.2em] uppercase border border-brand-red bg-brand-red text-white hover:bg-brand-red/90 transition-colors px-3 py-2 rounded disabled:opacity-50"
                        >
                            {ensuring ? <Loader2 className="w-3 h-3 animate-spin" /> : <User className="w-3 h-3" />}
                            {ensuring ? "Setting up…" : "This client trains themselves"}
                        </button>
                        <button
                            type="button"
                            data-testid="lead-schedule-add-athlete"
                            onClick={onAddAthlete}
                            disabled={ensuring}
                            className="inline-flex items-center justify-center gap-1.5 text-[10px] tracking-[0.2em] uppercase border border-zinc-800 hover:border-brand-red hover:text-brand-red transition-colors px-3 py-2 rounded text-zinc-300"
                        >
                            <Plus className="w-3 h-3" /> Add a kid / athlete
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Single athlete → just inline the schedule panel directly.
    if (athletes.length === 1) {
        const a = athletes[0];
        return (
            <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                    <CoverageDot coverage={a.coverage} athleteId={a.athlete_id} />
                    <span className="text-[11px] tracking-[0.2em] uppercase text-zinc-400">
                        {a.coverage?.label || (a.coverage?.covered ? "Covered" : "No active coverage")}
                    </span>
                </div>
                <AthleteSchedulePanel athleteId={a.athlete_id} athleteFirstName={a.first_name} />
                <div className="mt-4 text-center">
                    <Link
                        to={`/coach/athletes/${a.athlete_id}?tab=schedule`}
                        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-brand-red"
                    >
                        Open full dossier <ChevronRight className="w-3 h-3" />
                    </Link>
                </div>
            </div>
        );
    }

    // Multiple athletes → chip picker + the selected athlete's panel.
    const selectedId = scheduleAthleteId || athletes[0].athlete_id;
    const selected = athletes.find((a) => a.athlete_id === selectedId) || athletes[0];
    return (
        <div className="p-6">
            <div data-testid="lead-schedule-athlete-picker" className="flex gap-2 flex-wrap mb-5">
                {athletes.map((a) => {
                    const on = a.athlete_id === selected.athlete_id;
                    return (
                        <button
                            key={a.athlete_id}
                            type="button"
                            data-testid={`lead-schedule-pick-${a.athlete_id}`}
                            onClick={() => setScheduleAthleteId(a.athlete_id)}
                            className={`inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase px-3 py-2 border transition-colors ${
                                on
                                    ? "border-brand-red bg-brand-red/10 text-white"
                                    : "border-zinc-800 text-zinc-300 hover:border-zinc-700 hover:text-white"
                            }`}
                        >
                            <CoverageDot coverage={a.coverage} athleteId={a.athlete_id} size="xs" />
                            {a.first_name} {a.last_name}
                        </button>
                    );
                })}
            </div>
            <div className="flex items-center gap-2 mb-3">
                <CoverageDot coverage={selected.coverage} athleteId={selected.athlete_id} />
                <span className="text-[11px] tracking-[0.2em] uppercase text-zinc-400">
                    {selected.coverage?.label || (selected.coverage?.covered ? "Covered" : "No active coverage")}
                </span>
            </div>
            <AthleteSchedulePanel athleteId={selected.athlete_id} athleteFirstName={selected.first_name} />
            <div className="mt-4 text-center">
                <Link
                    to={`/coach/athletes/${selected.athlete_id}?tab=schedule`}
                    className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-brand-red"
                >
                    Open {selected.first_name}&apos;s full dossier <ChevronRight className="w-3 h-3" />
                </Link>
            </div>
        </div>
    );
}
